import React, { useState, useEffect, createContext, useContext, useMemo, useCallback } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation, useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";

// Pages
import LandingPage from "./pages/LandingPage";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import CustomerDashboard from "./pages/CustomerDashboard";
import ProfessionalDashboard from "./pages/ProfessionalDashboard";
import ProfessionalProfile from "./pages/ProfessionalProfile";
import PostJobPage from "./pages/PostJobPage";
import BrowseLeadsPage from "./pages/BrowseLeadsPage";
import PricingPage from "./pages/PricingPage";
import SubscriptionSuccess from "./pages/SubscriptionSuccess";
import JobDetailPage from "./pages/JobDetailPage";
import TermsPage from "./pages/TermsPage";
import PrivacyPage from "./pages/PrivacyPage";
import AdminLoginPage from "./pages/AdminLoginPage";
import AdminDashboard from "./pages/AdminDashboard";
import VerificationPage from "./pages/VerificationPage";
import CategoriesPage from "./pages/CategoriesPage";
import PublicProfilePage from "./pages/PublicProfilePage";
import ReviewsPage from "./pages/ReviewsPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import MessagesPage from "./pages/MessagesPage";
import AnalyticsPage from "./pages/AnalyticsPage";
import ProfileSettingsPage from "./pages/ProfileSettingsPage";
import BrowseProfessionalsPage from "./pages/BrowseProfessionalsPage";
import LocalServicePage from "./pages/LocalServicePage";
import ProLeadsPage from "./pages/ProLeadsPage";
import ProConversationsPage from "./pages/ProConversationsPage";
import ProClientsPage from "./pages/ProClientsPage";
import ProTasksPage from "./pages/ProTasksPage";
import ProEstimatesPage from "./pages/ProEstimatesPage";
import ProAppointmentsPage from "./pages/ProAppointmentsPage";
import ProSchedulePage from "./pages/ProSchedulePage";
import ProTimeClockPage from "./pages/ProTimeClockPage";
import ProReportsPage from "./pages/ProReportsPage";
import ProLeadsMapPage from "./pages/ProLeadsMapPage";
import NotificationSettingsPage from "./pages/NotificationSettingsPage";
import ProRegistrationSuccessPage from "./pages/ProRegistrationSuccessPage";
import ProBillingPage from "./pages/ProBillingPage";

// Components
import ChatButton from "./components/ChatButton";
import PWAInstallPrompt from "./components/PWAInstallPrompt";
import SplashScreen from "./components/SplashScreen";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("token"));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const verifyToken = async () => {
      if (token) {
        try {
          const response = await axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${token}` },
            withCredentials: true
          });
          setUser(response.data);
        } catch (error) {
          localStorage.removeItem("token");
          setToken(null);
          setUser(null);
        }
      }
      setLoading(false);
    };
    verifyToken();
  }, [token]);

  const login = async (email, password) => {
    // Use secure cookie-based login with token fallback
    const response = await axios.post(`${API}/auth/login`, { email, password }, {
      withCredentials: true
    });
    const { access_token, user: userData } = response.data;
    localStorage.setItem("token", access_token);
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const registerCustomer = async (data) => {
    const response = await axios.post(`${API}/auth/register/customer`, data, {
      withCredentials: true
    });
    const { access_token, user: userData } = response.data;
    localStorage.setItem("token", access_token);
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const registerProfessional = async (data) => {
    const response = await axios.post(`${API}/auth/register/professional`, data, {
      withCredentials: true
    });
    const { access_token, user: userData } = response.data;
    localStorage.setItem("token", access_token);
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (error) {
      // Log error but continue with local logout to ensure user can always log out
      if (process.env.NODE_ENV === 'development') {
        console.warn('Logout API call failed:', error.message);
      }
    }
    localStorage.removeItem("token");
    setToken(null);
    setUser(null);
  };

  const refreshUser = useCallback(async () => {
    if (token) {
      try {
        const response = await axios.get(`${API}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true
        });
        setUser(response.data);
      } catch (error) {
        setUser(null);
        localStorage.removeItem("token");
      }
    }
  }, [token]);

  const authValue = useMemo(() => ({
    user,
    token,
    loading,
    login,
    registerCustomer,
    registerProfessional,
    logout,
    refreshUser
  }), [user, token, loading, refreshUser]);

  return (
    <AuthContext.Provider value={authValue}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route
const ProtectedRoute = ({ children, allowedTypes }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  
  // Memoize the state object to prevent unnecessary re-renders
  const loginRedirectState = useMemo(() => ({ from: location }), [location]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-sand">
        <div className="animate-pulse text-forest font-heading text-xl">Loading...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={loginRedirectState} replace />;
  }

  if (allowedTypes && !allowedTypes.includes(user.user_type)) {
    return <Navigate to="/" replace />;
  }

  return children;
};

// Route type constants to prevent re-renders
const CUSTOMER_ONLY = ["customer"];
const PROFESSIONAL_ONLY = ["professional"];
const CUSTOMER_OR_PROFESSIONAL = ["customer", "professional"];

function App() {
  const [showSplash, setShowSplash] = useState(() => {
    // Only show splash on PWA standalone mode or first visit
    const isStandalone = window.matchMedia("(display-mode: standalone)").matches || 
                         window.navigator.standalone === true;
    const hasSeenSplash = sessionStorage.getItem("splashShown");
    return isStandalone && !hasSeenSplash;
  });

  const handleSplashComplete = () => {
    sessionStorage.setItem("splashShown", "true");
    setShowSplash(false);
  };

  return (
    <BrowserRouter>
      <AuthProvider>
        {showSplash && <SplashScreen onComplete={handleSplashComplete} />}
        <Toaster position="top-right" richColors closeButton />
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/register/customer" element={<RegisterPage type="customer" />} />
          <Route path="/register/professional" element={<RegisterPage type="professional" />} />
          <Route path="/register/professional/success" element={<ProRegistrationSuccessPage />} />
          <Route path="/register/professional/payment" element={<RegisterPage type="professional" />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/pricing" element={<PricingPage />} />
          <Route path="/categories" element={<CategoriesPage />} />
          <Route
            path="/leads"
            element={
              <ProtectedRoute allowedTypes={PROFESSIONAL_ONLY}>
                <BrowseLeadsPage />
              </ProtectedRoute>
            }
          />
          <Route path="/job/:jobId" element={<JobDetailPage />} />
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute allowedTypes={CUSTOMER_ONLY}>
                <CustomerDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/pro/dashboard"
            element={
              <ProtectedRoute allowedTypes={PROFESSIONAL_ONLY}>
                <ProfessionalDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/pro/leads"
            element={
              <ProtectedRoute allowedTypes={PROFESSIONAL_ONLY}>
                <ProLeadsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/pro/conversations"
            element={
              <ProtectedRoute allowedTypes={PROFESSIONAL_ONLY}>
                <ProConversationsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/pro/clients"
            element={
              <ProtectedRoute allowedTypes={PROFESSIONAL_ONLY}>
                <ProClientsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/pro/tasks"
            element={
              <ProtectedRoute allowedTypes={PROFESSIONAL_ONLY}>
                <ProTasksPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/pro/estimates"
            element={
              <ProtectedRoute allowedTypes={PROFESSIONAL_ONLY}>
                <ProEstimatesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/pro/appointments"
            element={
              <ProtectedRoute allowedTypes={PROFESSIONAL_ONLY}>
                <ProAppointmentsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/pro/schedule"
            element={
              <ProtectedRoute allowedTypes={PROFESSIONAL_ONLY}>
                <ProSchedulePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/pro/timeclock"
            element={
              <ProtectedRoute allowedTypes={PROFESSIONAL_ONLY}>
                <ProTimeClockPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/pro/reports"
            element={
              <ProtectedRoute allowedTypes={PROFESSIONAL_ONLY}>
                <ProReportsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/pro/leads-map"
            element={
              <ProtectedRoute allowedTypes={PROFESSIONAL_ONLY}>
                <ProLeadsMapPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/pro/notification-settings"
            element={
              <ProtectedRoute allowedTypes={PROFESSIONAL_ONLY}>
                <NotificationSettingsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/pro/billing"
            element={
              <ProtectedRoute allowedTypes={PROFESSIONAL_ONLY}>
                <ProBillingPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/pro/verify"
            element={
              <ProtectedRoute allowedTypes={PROFESSIONAL_ONLY}>
                <VerificationPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/pro/profile"
            element={
              <ProtectedRoute allowedTypes={PROFESSIONAL_ONLY}>
                <ProfessionalProfile />
              </ProtectedRoute>
            }
          />
          <Route path="/professional/:professionalId" element={<PublicProfilePage />} />
          <Route
            path="/reviews"
            element={
              <ProtectedRoute allowedTypes={CUSTOMER_OR_PROFESSIONAL}>
                <ReviewsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/messages"
            element={
              <ProtectedRoute allowedTypes={CUSTOMER_OR_PROFESSIONAL}>
                <MessagesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/pro/analytics"
            element={
              <ProtectedRoute allowedTypes={PROFESSIONAL_ONLY}>
                <AnalyticsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/post-job"
            element={
              <ProtectedRoute allowedTypes={CUSTOMER_ONLY}>
                <PostJobPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/subscription/success"
            element={
              <ProtectedRoute allowedTypes={PROFESSIONAL_ONLY}>
                <SubscriptionSuccess />
              </ProtectedRoute>
            }
          />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/cookies" element={<PrivacyPage />} />
          <Route path="/professionals" element={<BrowseProfessionalsPage />} />
          <Route path="/services/:service/:city" element={<LocalServicePage />} />
          <Route
            path="/settings"
            element={
              <ProtectedRoute allowedTypes={CUSTOMER_OR_PROFESSIONAL}>
                <ProfileSettingsPage />
              </ProtectedRoute>
            }
          />
          <Route path="/admin" element={<AdminLoginPage />} />
          <Route path="/admin/dashboard" element={<AdminDashboard />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <ChatButton />
        <PWAInstallPrompt />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
